import {
  moonPhase,
  nextLunarPhaseEvent,
  type LunarEvent,
} from "@nav-games/astro";

/// Moon-phase reference panel — today's phase, illumination and age,
/// the dates of the next four phase events, and a simple disc
/// diagram. Visibility-planning info you'd otherwise read off the
/// Almanac's monthly Moon page.

interface MoonPhasePanelProps {
  /// Defaults to "now" (re-evaluates per render — fine for a static
  /// Reference page; for a long-lived view pass a fixed Date).
  date?: Date;
}

const PHASE_EVENTS: readonly { event: LunarEvent; label: string }[] = [
  { event: "new", label: "New Moon" },
  { event: "first-quarter", label: "First Quarter" },
  { event: "full", label: "Full Moon" },
  { event: "last-quarter", label: "Last Quarter" },
];

function MoonDisc({
  illumination,
  waxing,
  size = 80,
}: {
  illumination: number;
  waxing: boolean;
  size?: number;
}) {
  const r = size / 2 - 2;
  const cx = size / 2;
  const cy = size / 2;

  // Build a half-moon then sweep an inner ellipse for the terminator.
  // illumination 0 → fully dark; 0.5 → exactly half; 1 → fully lit.
  // For the waxing half, the LIT side is on the right; for waning,
  // on the left.
  const k = 1 - 2 * illumination; // -1 (full) … 0 (half) … 1 (new)
  // rx of the terminator ellipse — narrower (closer to 0) means the
  // terminator hugs the centre line, i.e. closer to half-illumination.
  const rx = Math.abs(k) * r;

  // The lit area is everything to the right of the terminator (for
  // waxing) or to its left (for waning). We render two arcs:
  // - The bright half-disc on the right (or left).
  // - The inner ellipse "cut-out" representing the terminator.

  const litColor = "#f5e4a1";
  const darkColor = "var(--bg-elev, #f8fafc)";
  const stroke = "var(--text, #0f172a)";

  // For an empty moon (illumination near 0), the inner ellipse covers
  // the entire half disc — render dark. For a full moon, it's bright.

  const rightLit = waxing;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`Moon disc — ${Math.round(illumination * 100)}% illuminated`}
    >
      {/* Background full disc — dark side colour. */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={illumination > 0.99 ? litColor : darkColor}
        stroke={stroke}
        strokeWidth={1}
      />
      {/* Lit half-disc (sweep from top-to-bottom on the lit side). */}
      {illumination > 0.001 && illumination < 0.999 && (
        <>
          <path
            d={
              rightLit
                ? `M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx} ${cy + r} Z`
                : `M ${cx} ${cy - r} A ${r} ${r} 0 0 0 ${cx} ${cy + r} Z`
            }
            fill={litColor}
          />
          {/* Terminator ellipse — fills with lit colour if illum>0.5
              (gibbous), dark colour if <0.5 (crescent). */}
          <ellipse
            cx={cx}
            cy={cy}
            rx={rx}
            ry={r}
            fill={illumination > 0.5 ? litColor : darkColor}
          />
        </>
      )}
    </svg>
  );
}

export function MoonPhasePanel({ date }: MoonPhasePanelProps) {
  const now = date ?? new Date();
  const phase = moonPhase(now);

  const fmtDate = (d: Date): string =>
    d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    }) + " UT";

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Moon phase right now</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        Computed from the geocentric elongation of Sun and Moon —
        illumination is <code>(1 − cos φ)/2</code> where φ is the
        phase angle (0° = new, 180° = full). The next-event dates use a
        uniform synodic-cycle estimate, accurate to roughly ±12 hours.
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 12,
        }}
      >
        <MoonDisc illumination={phase.illumination} waxing={phase.waxing} size={88} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{phase.name}</div>
          <div className="small muted">
            {(phase.illumination * 100).toFixed(0)}% illuminated —{" "}
            {phase.waxing ? "waxing" : "waning"}
          </div>
          <div className="small muted">
            Age: {phase.ageDays.toFixed(1)} days since the last new moon
          </div>
          <div className="small muted">
            Phase angle: {phase.phaseAngleDeg.toFixed(1)}°
          </div>
        </div>
      </div>

      <table
        className="small"
        style={{ width: "100%", borderCollapse: "collapse" }}
      >
        <thead>
          <tr style={{ textAlign: "left", color: "var(--text-muted)" }}>
            <th style={{ padding: "4px 4px" }}>Next event</th>
            <th style={{ padding: "4px 4px" }}>Date (UT)</th>
            <th style={{ padding: "4px 4px", textAlign: "right" }}>
              Days away
            </th>
          </tr>
        </thead>
        <tbody>
          {PHASE_EVENTS.map(({ event, label }) => {
            const d = nextLunarPhaseEvent(now, event);
            const days = (d.getTime() - now.getTime()) / 86_400_000;
            return (
              <tr key={event}>
                <td style={{ padding: "4px 4px" }}>{label}</td>
                <td style={{ padding: "4px 4px" }}>{fmtDate(d)}</td>
                <td
                  style={{ padding: "4px 4px", textAlign: "right" }}
                  className="muted"
                >
                  {days.toFixed(1)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
